/**
 * Generates an ordered array of smart short code candidates based on a company name.
 * Example: "Swastik Private Limited"
 * -> Candidate 0: "SPL" (Initials of all words)
 * -> Candidate 1: "SPLD" (Initials + last letter of last word)
 * -> Candidate 2: "SWPL" (First 2 letters of 1st word + initials of remaining)
 * -> Candidate 3: "SWA" (First 3 letters of 1st word)
 * -> Candidate 4: "SWAS" (First 4 letters of 1st word)
 * -> Candidate 5+: "SPL1", "SPL2", "SPL3"...
 */
export function generateSmartShortCodeCandidates(companyName: string): string[] {
  const clean = companyName.toUpperCase().replace(/[^A-Z0-9\s]/g, '').trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0) return ['COMP1', 'COMP2', 'COMP3'];

  const candidates: string[] = [];

  // Candidate 0: Initials of all words (e.g. SPL)
  const initials = words.map((w) => w[0]).join('');
  if (initials) candidates.push(initials);

  // Candidate 1: Initials + last letter of last word (e.g. SPLD from Swastik Private LimiteD)
  if (words.length >= 1) {
    const lastWord = words[words.length - 1];
    if (lastWord.length > 1) {
      candidates.push(initials + lastWord[lastWord.length - 1]);
    }
  }

  // Candidate 2: First 2 letters of 1st word + initials of remaining (e.g. SWPL from SWastik Private Limited)
  if (words[0].length >= 2 && words.length > 1) {
    candidates.push(words[0].slice(0, 2) + words.slice(1).map((w) => w[0]).join(''));
  }

  // Candidate 3: First 3 letters of 1st word (e.g. SWA from SWAstik)
  if (words[0].length >= 3) {
    candidates.push(words[0].slice(0, 3));
  }

  // Candidate 4: First 4 letters of 1st word (e.g. SWAS from SWAStik)
  if (words[0].length >= 4) {
    candidates.push(words[0].slice(0, 4));
  }

  // Candidate 5..N: Base initials + numeric counter (SPL1, SPL2, etc.)
  const base = initials || (words[0] ? words[0].slice(0, 3) : 'CO');
  for (let i = 1; i <= 99; i++) {
    candidates.push(`${base}${i}`);
  }

  // Clean & sanitize (uppercase, max 10 chars, non-empty, remove duplicates)
  const uniqueCandidates: string[] = [];
  for (const c of candidates) {
    const sanitized = c.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    if (sanitized && !uniqueCandidates.includes(sanitized)) {
      uniqueCandidates.push(sanitized);
    }
  }

  return uniqueCandidates;
}
